import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { createPatchData } from 'features/fde/helpers/rolodex/createPatchData'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { type LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import { extractMentionedUserIds } from 'features/richText/utils/entriesFromRichText'
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createLibraUrl } from 'routes/libra/-searchParams'
import { RichTextContent } from 'types'
import { v4 } from 'uuid'

import {
  anchorBelongsToZone,
  buildAnchorIndex,
  getThreadsForAnchorFromIndex,
} from './anchorMatching'
import {
  type ThreadMetaEntry,
  useCreateComment,
  useCreateCommentThread,
  useDeleteComment,
  useDeleteThread,
  useReopenThread,
  useResolveThread,
  useTicketComments,
} from './commentsApi'
import type {
  Comment,
  CommentAnchor,
  CommentThread,
  CommentWithThread,
  StepId,
  ThreadId,
} from './commentTypes'
import { createCommentNotifications } from './createCommentNotifications'

export type CommentViewMode = 'current-step' | 'all-steps'
export type CommentFilterMode = 'active' | 'resolved'

export interface CommentsContextType {
  threads: ReadonlyArray<CommentThread>
  isLoading: boolean
  getThreadsForAnchor: (anchor: CommentAnchor) => ReadonlyArray<CommentThread>
  getThreadCountForStep: (stepId: StepId | string) => number
  getUnresolvedCountForStep: (stepId: StepId | string) => number
  createThread: (
    anchor: CommentAnchor,
    text: RichTextContent,
    valueSnapshot?: unknown
  ) => Promise<void>
  replyToThread: (threadId: ThreadId, text: RichTextContent) => Promise<void>
  resolveThread: (threadId: ThreadId) => Promise<void>
  reopenThread: (threadId: ThreadId) => Promise<void>
  deleteThread: (threadId: ThreadId | string) => Promise<void>
  deleteComment: (
    commentId: string,
    threadId: ThreadId | string
  ) => Promise<void>
  isSidebarVisible: boolean
  toggleSidebar: () => void
  focusedAnchor: CommentAnchor | null
  setFocusedAnchor: (anchor: CommentAnchor | null) => void
  composingAnchor: CommentAnchor | null
  startComposing: (anchor: CommentAnchor) => void
  stopComposing: () => void
  expandedThreadId: string | null
  expandThread: (threadId: ThreadId | string) => void
  collapseThread: () => void
  focusThread: (threadId: ThreadId | string) => void
  clearSelection: () => void
  viewMode: CommentViewMode
  setViewMode: (mode: CommentViewMode) => void
  filterMode: CommentFilterMode
  setFilterMode: (mode: CommentFilterMode) => void
}

const CommentsContext = createContext<CommentsContextType | undefined>(
  undefined
)

export const CommentsProvider: FC<{
  ticket: LibraTicket
  ticketGroupOwners?: string[]
  initialThreadId?: string
  children: ReactNode
}> = ({ ticket, ticketGroupOwners = [], initialThreadId, children }) => {
  const { user } = useGeckoPermissions()
  const { errorNotification } = useGeckoNotifications()
  const { rolodexPatch } = useRolodexEdit()
  const { ALERTS_URL, BASE_PATH } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const {
    threadMetas,
    comments: rawComments,
    isLoading,
    forceRefresh,
  } = useTicketComments(ticket.id)

  const [isSidebarVisible, setIsSidebarVisible] = useState(false)
  const [focusedAnchor, setFocusedAnchor] = useState<CommentAnchor | null>(null)
  const [composingAnchor, setComposingAnchor] = useState<CommentAnchor | null>(
    null
  )
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<CommentViewMode>('current-step')
  const [filterMode, setFilterMode] = useState<CommentFilterMode>('active')

  const [threadOverrides, setThreadOverrides] = useState<
    Map<string, ThreadMetaEntry | null>
  >(() => new Map())

  const [commentOverrides, setCommentOverrides] = useState<
    Map<string, CommentWithThread | null>
  >(() => new Map())

  const setThreadOverride = useCallback(
    (id: string, meta: ThreadMetaEntry | null) => {
      setThreadOverrides((prev) => new Map(prev).set(id, meta))
    },
    []
  )

  const clearThreadOverride = useCallback((id: string) => {
    setThreadOverrides((prev) => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  const setCommentOverride = useCallback(
    (id: string, comment: CommentWithThread | null) => {
      setCommentOverrides((prev) => new Map(prev).set(id, comment))
    },
    []
  )

  const clearCommentOverride = useCallback((id: string) => {
    setCommentOverrides((prev) => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }, [])

  const effectiveThreadMetas = useMemo<ReadonlyArray<ThreadMetaEntry>>(() => {
    if (threadOverrides.size === 0) return threadMetas

    const merged: ThreadMetaEntry[] = []
    const seen = new Set<string>()

    for (const meta of threadMetas) {
      const id = meta.entryId as string
      seen.add(id)
      if (threadOverrides.has(id)) {
        const override = threadOverrides.get(id)
        if (override !== null && override !== undefined) merged.push(override)
      } else {
        merged.push(meta)
      }
    }

    for (const [id, meta] of threadOverrides) {
      if (!seen.has(id) && meta !== null && meta !== undefined) {
        merged.push(meta)
      }
    }

    return merged
  }, [threadMetas, threadOverrides])

  const effectiveComments = useMemo<ReadonlyArray<CommentWithThread>>(() => {
    if (commentOverrides.size === 0) return rawComments

    const merged: CommentWithThread[] = []
    const seen = new Set<string>()

    for (const comment of rawComments) {
      seen.add(comment.id)
      if (commentOverrides.has(comment.id)) {
        const override = commentOverrides.get(comment.id)
        if (override !== null && override !== undefined) merged.push(override)
      } else {
        merged.push(comment)
      }
    }

    for (const [id, comment] of commentOverrides) {
      if (!seen.has(id) && comment !== null && comment !== undefined) {
        merged.push(comment)
      }
    }

    return merged
  }, [rawComments, commentOverrides])

  const displayThreads = useMemo<ReadonlyArray<CommentThread>>(() => {
    const byThread = new Map<string, Comment[]>()
    for (const c of effectiveComments) {
      const list = byThread.get(c.threadId)
      if (list) list.push(c)
      else byThread.set(c.threadId, [c])
    }
    for (const list of byThread.values()) {
      list.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    }
    return effectiveThreadMetas.map((meta) => ({
      ...meta,
      comments: byThread.get(meta.entryId as string) ?? [],
    }))
  }, [effectiveThreadMetas, effectiveComments])

  const anchorIndex = useMemo(
    () => buildAnchorIndex(displayThreads),
    [displayThreads]
  )

  const getThreadsForAnchor = useCallback(
    (anchor: CommentAnchor) =>
      getThreadsForAnchorFromIndex(anchorIndex, anchor),
    [anchorIndex]
  )

  const getThreadCountForStep = useCallback(
    (stepId: string) => anchorIndex.get(stepId)?.length ?? 0,
    [anchorIndex]
  )

  const getUnresolvedCountForStep = useCallback(
    (stepId: string) =>
      anchorIndex.get(stepId)?.filter((t) => !t.resolved).length ?? 0,
    [anchorIndex]
  )

  const createThreadApi = useCreateCommentThread()
  const createCommentApi = useCreateComment()
  const resolveApi = useResolveThread()
  const reopenApi = useReopenThread()
  const deleteThreadApi = useDeleteThread()
  const deleteCommentApi = useDeleteComment()

  const authorId = user?.id ?? user?.email ?? 'unknown'
  const authorName = user?.name ?? user?.email ?? 'unknown'
  const buildTicketUrl = useCallback(
    (threadId: string) =>
      `${BASE_PATH}${createLibraUrl({
        orgId: ticket.tags.organization ?? undefined,
        workflowId: ticket.workflowId,
        ticket: ticket.id,
        thread: threadId,
      })}`,
    [BASE_PATH, ticket.tags.organization, ticket.workflowId, ticket.id]
  )

  const threadById = useMemo(
    () => new Map(displayThreads.map((t) => [t.entryId as string, t])),
    [displayThreads]
  )

  const createThread = useCallback(
    async (
      anchor: CommentAnchor,
      text: RichTextContent,
      valueSnapshot?: unknown
    ) => {
      const tempThreadId = `temp-thread-${v4()}` as ThreadId
      const tempCommentId = `temp-comment-${v4()}`
      const now = new Date().toISOString()

      const users = [
        ...new Set([
          authorId,
          ...ticketGroupOwners,
          ...extractMentionedUserIds(text),
        ]),
      ]

      setThreadOverride(tempThreadId as string, {
        entryId: tempThreadId,
        anchor,
        resolved: false,
        users,
        ...(valueSnapshot !== undefined
          ? { valueAtCreation: valueSnapshot }
          : {}),
      })
      setCommentOverride(tempCommentId, {
        id: tempCommentId,
        author: authorId,
        authorName,
        text,
        createdAt: now,
        threadId: tempThreadId as string,
      })

      try {
        const { threadId } = await createThreadApi({
          ticket,
          workflowId: ticket.workflowId,
          anchor,
          text,
          authorId,
          authorName,
          users,
          valueSnapshot,
        })
        void createCommentNotifications({
          text,
          ticketTitle: ticket.title,
          subtitle: ticket.createdBy
            ? `Author: ${ticket.createdBy}`
            : undefined,
          organizationId: ticket.tags.organization ?? '',
          recipientUserIds: users,
          authorId,
          commentAuthorName: authorName,
          alertsUrl: ALERTS_URL,
          axios: authorizedAxios,
          ticketUrl: buildTicketUrl(threadId),
        })
        await forceRefresh()
      } catch (err) {
        console.error('Failed to create comment thread:', err)
        errorNotification('Failed to create comment. Please try again.')
      } finally {
        clearThreadOverride(tempThreadId as string)
        clearCommentOverride(tempCommentId)
      }
    },
    [
      createThreadApi,
      ticket,
      authorId,
      authorName,
      ticketGroupOwners,
      setThreadOverride,
      setCommentOverride,
      clearThreadOverride,
      clearCommentOverride,
      forceRefresh,
      errorNotification,
      ALERTS_URL,
      authorizedAxios,
      buildTicketUrl,
    ]
  )

  const replyToThread = useCallback(
    async (threadId: string, text: RichTextContent) => {
      const thread = threadById.get(threadId)
      if (!thread) {
        console.warn(`replyToThread: thread ${threadId} not found (stale?)`)
        return
      }

      const tempCommentId = `temp-comment-${v4()}`
      const now = new Date().toISOString()

      const currentThreadUsers = thread.users ?? []
      const newUsers = [
        ...new Set([
          ...currentThreadUsers,
          authorId,
          ...ticketGroupOwners,
          ...extractMentionedUserIds(text),
        ]),
      ]

      setCommentOverride(tempCommentId, {
        id: tempCommentId,
        author: authorId,
        authorName,
        text,
        createdAt: now,
        threadId,
      })

      if (newUsers.length > currentThreadUsers.length) {
        setThreadOverride(threadId, {
          ...thread,
          users: newUsers,
        })
      }

      try {
        await createCommentApi({
          ticket,
          workflowId: ticket.workflowId,
          threadId,
          text,
          authorId,
          authorName,
        })
        void createCommentNotifications({
          text,
          ticketTitle: ticket.title,
          subtitle: ticket.createdBy
            ? `Author: ${ticket.createdBy}`
            : undefined,
          organizationId: ticket.tags.organization ?? '',
          recipientUserIds: newUsers,
          authorId,
          commentAuthorName: authorName,
          alertsUrl: ALERTS_URL,
          axios: authorizedAxios,
          ticketUrl: buildTicketUrl(threadId),
        })

        if (newUsers.length > currentThreadUsers.length) {
          const patch = createPatchData(
            threadId,
            { users: currentThreadUsers },
            { users: newUsers }
          )
          if (patch) await rolodexPatch([patch])
        }

        await forceRefresh()
      } catch (err) {
        console.error('Failed to reply to thread:', err)
        errorNotification('Failed to post reply. Please try again.')
      } finally {
        clearCommentOverride(tempCommentId)
        clearThreadOverride(threadId)
      }
    },
    [
      threadById,
      createCommentApi,
      rolodexPatch,
      ticket,
      authorId,
      authorName,
      ticketGroupOwners,
      setCommentOverride,
      setThreadOverride,
      clearCommentOverride,
      clearThreadOverride,
      forceRefresh,
      errorNotification,
      ALERTS_URL,
      authorizedAxios,
      buildTicketUrl,
    ]
  )

  const resolveThread = useCallback(
    async (threadId: string) => {
      const thread = threadById.get(threadId)
      if (!thread) {
        console.warn(`resolveThread: thread ${threadId} not found (stale?)`)
        return
      }

      const now = new Date().toISOString()
      setThreadOverride(threadId, {
        entryId: thread.entryId,
        anchor: thread.anchor,
        resolved: true,
        resolvedBy: authorId,
        resolvedByName: authorName,
        resolvedAt: now,
        valueAtCreation: thread.valueAtCreation,
        users: thread.users ?? [],
      })

      try {
        await resolveApi(threadId, thread.resolved, authorId, authorName)
        await forceRefresh()
      } catch (err) {
        console.error('Failed to resolve thread:', err)
        errorNotification('Failed to resolve comment. Please try again.')
      } finally {
        clearThreadOverride(threadId)
      }
    },
    [
      threadById,
      resolveApi,
      authorId,
      setThreadOverride,
      clearThreadOverride,
      forceRefresh,
      errorNotification,
      authorName,
    ]
  )

  const reopenThread = useCallback(
    async (threadId: string) => {
      const thread = threadById.get(threadId)
      if (!thread) {
        console.warn(`reopenThread: thread ${threadId} not found (stale?)`)
        return
      }

      setThreadOverride(threadId, {
        entryId: thread.entryId,
        anchor: thread.anchor,
        resolved: false,
        resolvedBy: undefined,
        resolvedAt: undefined,
        valueAtCreation: thread.valueAtCreation,
        users: thread.users ?? [],
      })

      try {
        await reopenApi(
          threadId,
          thread.resolved,
          thread.resolvedBy,
          thread.resolvedAt
        )
        await forceRefresh()
      } catch (err) {
        console.error('Failed to reopen thread:', err)
        errorNotification('Failed to reopen comment. Please try again.')
      } finally {
        clearThreadOverride(threadId)
      }
    },
    [
      threadById,
      reopenApi,
      setThreadOverride,
      clearThreadOverride,
      forceRefresh,
      errorNotification,
    ]
  )

  const deleteThread = useCallback(
    async (threadId: string) => {
      const thread = threadById.get(threadId)
      const commentIds = thread?.comments.map((c) => c.id) ?? []

      setThreadOverride(threadId, null)
      for (const cid of commentIds) {
        setCommentOverride(cid, null)
      }

      try {
        await deleteThreadApi(threadId, commentIds)
        await forceRefresh()
      } catch (err) {
        console.error('Failed to delete thread:', err)
        errorNotification('Failed to delete comment. Please try again.')
      } finally {
        clearThreadOverride(threadId)
        for (const cid of commentIds) {
          clearCommentOverride(cid)
        }
      }
    },
    [
      threadById,
      deleteThreadApi,
      setThreadOverride,
      setCommentOverride,
      clearThreadOverride,
      clearCommentOverride,
      forceRefresh,
      errorNotification,
    ]
  )

  const deleteComment = useCallback(
    async (commentId: string, threadId: string) => {
      const thread = threadById.get(threadId)
      const isLastComment = thread !== undefined && thread.comments.length <= 1

      setCommentOverride(commentId, null)
      if (isLastComment) {
        setThreadOverride(threadId, null)
      }

      try {
        await deleteCommentApi(commentId)
        if (isLastComment) {
          const remainingCommentIds = (thread?.comments ?? [])
            .filter((c) => c.id !== commentId)
            .map((c) => c.id)
          await deleteThreadApi(threadId, remainingCommentIds)
        }
        await forceRefresh()
      } catch (err) {
        console.error('Failed to delete comment:', err)
        errorNotification('Failed to delete comment. Please try again.')
      } finally {
        clearCommentOverride(commentId)
        if (isLastComment) {
          clearThreadOverride(threadId)
        }
      }
    },
    [
      threadById,
      deleteCommentApi,
      deleteThreadApi,
      setCommentOverride,
      setThreadOverride,
      clearCommentOverride,
      clearThreadOverride,
      forceRefresh,
      errorNotification,
    ]
  )

  // ── UI state callbacks ───────────────────────────────────────────────

  const toggleSidebar = useCallback(() => {
    setIsSidebarVisible((prev) => !prev)
  }, [])

  const startComposing = useCallback((anchor: CommentAnchor) => {
    setComposingAnchor(anchor)
    setFocusedAnchor(anchor)
  }, [])

  const stopComposing = useCallback(() => {
    setComposingAnchor(null)
  }, [])

  const expandThread = useCallback((threadId: string) => {
    setExpandedThreadId(threadId)
  }, [])

  const collapseThread = useCallback(() => {
    setExpandedThreadId(null)
  }, [])

  const focusThread = useCallback(
    (threadId: string) => {
      const thread = threadById.get(threadId)
      if (!thread) return
      setExpandedThreadId(threadId)
      setFocusedAnchor(thread.anchor)
      if (!isSidebarVisible) setIsSidebarVisible(true)
    },
    [threadById, isSidebarVisible]
  )

  const clearSelection = useCallback(() => {
    setExpandedThreadId(null)
    setFocusedAnchor(null)
    setComposingAnchor(null)
  }, [])

  const initialThreadFocused = useRef(false)
  useEffect(() => {
    if (!initialThreadId || initialThreadFocused.current) return
    const thread = threadById.get(initialThreadId)
    if (!thread) return
    initialThreadFocused.current = true
    setExpandedThreadId(initialThreadId)
    setFocusedAnchor(thread.anchor)
    setIsSidebarVisible(true)
  }, [initialThreadId, threadById])

  const value = useMemo<CommentsContextType>(
    () => ({
      threads: displayThreads,
      isLoading,
      getThreadsForAnchor,
      getThreadCountForStep,
      getUnresolvedCountForStep,
      createThread,
      replyToThread,
      resolveThread,
      reopenThread,
      deleteThread,
      deleteComment,
      isSidebarVisible,
      toggleSidebar,
      focusedAnchor,
      setFocusedAnchor,
      composingAnchor,
      startComposing,
      stopComposing,
      expandedThreadId,
      expandThread,
      collapseThread,
      focusThread,
      clearSelection,
      viewMode,
      setViewMode,
      filterMode,
      setFilterMode,
    }),
    [
      displayThreads,
      isLoading,
      getThreadsForAnchor,
      getThreadCountForStep,
      getUnresolvedCountForStep,
      createThread,
      replyToThread,
      resolveThread,
      reopenThread,
      deleteThread,
      deleteComment,
      isSidebarVisible,
      toggleSidebar,
      focusedAnchor,
      composingAnchor,
      startComposing,
      stopComposing,
      expandedThreadId,
      expandThread,
      collapseThread,
      focusThread,
      clearSelection,
      viewMode,
      filterMode,
    ]
  )

  return (
    <CommentsContext.Provider value={value}>
      {children}
    </CommentsContext.Provider>
  )
}

export function useComments(): CommentsContextType {
  const ctx = useContext(CommentsContext)
  if (!ctx) {
    throw new Error('useComments must be used within a CommentsProvider')
  }
  return ctx
}

export function useCommentsForZone(
  stepId: string,
  zoneId: string
): ReadonlyArray<CommentThread> {
  const { threads } = useComments()
  return useMemo(
    () => threads.filter((t) => anchorBelongsToZone(t.anchor, stepId, zoneId)),
    [threads, stepId, zoneId]
  )
}

export function useCommentCount(): number {
  const { threads } = useComments()
  return useMemo(() => threads.filter((t) => !t.resolved).length, [threads])
}
